import styled from 'styled-components'
import type { FeasibilityStatus } from '@popety-io/types'

/* Layout */
export const Container = styled.div`
  padding: ${({ theme }) => theme.spacing.large};
`

export const SectionTitle = styled.h2`
  font-size: ${({ theme }) => theme.typography.fontSize.section};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.text.primary};
  margin: ${({ theme }) => theme.spacing.xlarge} 0
    ${({ theme }) => theme.spacing.medium} 0;

  &:first-of-type {
    margin-top: 0;
  }
`

export const Grid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${({ theme }) => theme.spacing.large};

  @media (max-width: 1200px) {
    grid-template-columns: 1fr;
  }
`

export const FullWidthSection = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing.large};
`

/* Assessment Banner */
export const AssessmentContainer = styled.div<{ $status: FeasibilityStatus }>`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing.medium};
  padding: ${({ theme }) => theme.spacing.medium}
    ${({ theme }) => theme.spacing.large};
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  margin-bottom: ${({ theme }) => theme.spacing.large};
  container-type: inline-size;
  background: ${({ $status }) =>
    $status === 'viable'
      ? 'linear-gradient(135deg, #ECFDF5 0%, #D1FAE5 100%)'
      : $status === 'marginal'
        ? 'linear-gradient(135deg, #FFFBEB 0%, #FEF3C7 100%)'
        : 'linear-gradient(135deg, #FEF2F2 0%, #FEE2E2 100%)'};
  border: 1px solid
    ${({ $status }) =>
      $status === 'viable'
        ? '#6EE7B7'
        : $status === 'marginal'
          ? '#FCD34D'
          : '#FCA5A5'};

  @supports not (container-type: inline-size) {
    @media (max-width: 768px) {
      flex-direction: column;
      gap: ${({ theme }) => theme.spacing.medium};
      align-items: flex-start;
    }
  }
`

export const StatusSection = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.medium};
  min-width: 0;
  flex: 1;
`

export const TrafficLight = styled.div<{ $status: FeasibilityStatus }>`
  flex: none;
  width: 48px;
  height: 48px;
  border-radius: ${({ theme }) => theme.borderRadius.circle};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  color: white;
  background: ${({ $status }) =>
    $status === 'viable'
      ? '#10B981'
      : $status === 'marginal'
        ? '#F59E0B'
        : '#EF4444'};
  box-shadow: 0 4px 12px
    ${({ $status }) =>
      $status === 'viable'
        ? 'rgba(16, 185, 129, 0.3)'
        : $status === 'marginal'
          ? 'rgba(245, 158, 11, 0.3)'
          : 'rgba(239, 68, 68, 0.3)'};
`

export const StatusText = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.xsmall};
  min-width: 0;
  flex: 1;
`

export const StatusTitle = styled.div<{ $status: FeasibilityStatus }>`
  font-size: 20px;
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  color: ${({ $status }) =>
    $status === 'viable'
      ? '#065F46'
      : $status === 'marginal'
        ? '#92400E'
        : '#991B1B'};
`

export const StatusDescription = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSize.body};
  color: ${({ theme }) => theme.colors.text.secondary};
`

export const MetricsPreview = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.xlarge};
  flex: none;

  @container (max-width: 800px) {
    flex: 1 1 100%;
    justify-content: flex-start;
    flex-wrap: wrap;
  }
`

export const MetricItemRight = styled.div`
  text-align: right;

  @container (max-width: 800px) {
    text-align: left;
  }
`

export const MetricLabelSmall = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSize.small};
  color: ${({ theme }) => theme.colors.text.tertiary};
  margin-bottom: ${({ theme }) => theme.spacing.xsmall};
`

export const MetricValueLarge = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSize.section};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.text.primary};
`

/* Summary Cards */
export const CardsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: ${({ theme }) => theme.spacing.medium};
`

export const Card = styled.div`
  padding: ${({ theme }) => theme.spacing.large};
  background: ${({ theme }) => theme.colors.background.primary};
  border: 1px solid ${({ theme }) => theme.colors.border.default};
  border-radius: ${({ theme }) => theme.borderRadius.medium};
`

export const CardLabel = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSize.small};
  color: ${({ theme }) => theme.colors.text.secondary};
  margin-bottom: ${({ theme }) => theme.spacing.default};
`

export const CardValue = styled.div<{
  $positive?: boolean
  $negative?: boolean
}>`
  font-size: 24px;
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  color: ${({ theme, $positive, $negative }) =>
    $positive
      ? theme.colors.success
      : $negative
        ? theme.colors.danger
        : theme.colors.text.primary};
`

export const CardSubtext = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSize.small};
  color: ${({ theme }) => theme.colors.text.tertiary};
  margin-top: ${({ theme }) => theme.spacing.xsmall};
`

/* Key Metrics Panel */
export const Panel = styled.div`
  background: ${({ theme }) => theme.colors.background.primary};
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  padding: ${({ theme }) => theme.spacing.large};
`

export const MetricRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${({ theme }) => theme.spacing.medium} 0;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border.light};

  &:last-child {
    border-bottom: none;
  }
`

export const MetricInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.xsmall};
`

export const MetricLabel = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSize.body};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  color: ${({ theme }) => theme.colors.text.primary};
`

export const MetricDescription = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSize.small};
  color: ${({ theme }) => theme.colors.text.tertiary};
`

export const MetricValue = styled.div<{
  $status?: 'good' | 'warning' | 'bad'
}>`
  font-size: 20px;
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  color: ${({ theme, $status }) =>
    $status === 'good'
      ? theme.colors.success
      : $status === 'bad'
        ? theme.colors.danger
        : $status === 'warning'
          ? theme.colors.warning
          : theme.colors.text.primary};
`

export const StatusBadge = styled.span<{
  $status: 'good' | 'warning' | 'bad'
}>`
  display: inline-block;
  padding: 2px 8px;
  font-size: ${({ theme }) => theme.typography.fontSize.micro};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  border-radius: 12px;
  margin-left: ${({ theme }) => theme.spacing.default};
  background: ${({ theme, $status }) =>
    $status === 'good'
      ? theme.colors.successLight
      : $status === 'warning'
        ? theme.colors.warningLight
        : '#FEE2E2'};
  color: ${({ theme, $status }) =>
    $status === 'good'
      ? theme.colors.success
      : $status === 'warning'
        ? '#B45309'
        : theme.colors.danger};
`

export const SectionHeader = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSize.small};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: ${({ theme }) => theme.colors.text.tertiary};
  margin-top: ${({ theme }) => theme.spacing.large};
  margin-bottom: ${({ theme }) => theme.spacing.default};

  &:first-child {
    margin-top: 0;
  }
`

/* Quick Facts */
export const FactsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: ${({ theme }) => theme.spacing.medium};
`

export const FactCard = styled.div`
  padding: ${({ theme }) => theme.spacing.medium};
  background: ${({ theme }) => theme.colors.background.primary};
  border: 1px solid ${({ theme }) => theme.colors.border.default};
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.medium};
`

export const FactIcon = styled.div`
  width: 40px;
  height: 40px;
  border-radius: ${({ theme }) => theme.borderRadius.default};
  background: ${({ theme }) => theme.colors.background.secondary};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`

export const FactContent = styled.div`
  flex: 1;
  min-width: 0;
`

export const FactLabel = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSize.small};
  color: ${({ theme }) => theme.colors.text.tertiary};
  margin-bottom: ${({ theme }) => theme.spacing.xsmall};
`

export const FactValue = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSize.body};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.text.primary};
`

/* Waterfall Chart */
export const ChartContainer = styled.div`
  width: 100%;
  height: 400px;
  background: ${({ theme }) => theme.colors.background.primary};
  border: 1px solid ${({ theme }) => theme.colors.border.default};
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  padding: ${({ theme }) => theme.spacing.medium};
`

/* Empty State */
export const EmptyContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: ${({ theme }) => theme.spacing.medium};
  padding: ${({ theme }) => theme.spacing.huge}
    ${({ theme }) => theme.spacing.large};
  text-align: center;
`

export const EmptyTitle = styled.div`
  font-size: 20px;
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.text.primary};
`

export const EmptyDescription = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSize.body};
  color: ${({ theme }) => theme.colors.text.secondary};
  max-width: 400px;
`
